import { clientLogger } from '@/lib/clientLogger';
import { useEffect, useState } from 'react';
import { ContactCreateDTO, ContactDTO, ContactLookupResultDTO, ContactUpdateDTO } from '@/types';
import { ChevronDown, Plus, UserCheck, X } from 'lucide-react';
import { apiService } from '@/lib/api';

import { createPortal } from 'react-dom';
import { useContactLookup } from '@/hooks/useContactLookup';
import { ContactPastJobsModal } from './ContactPastJobsModal';
import { JobDetailsModal } from '../JobDetailsModal';
import { useModalLayer, LayerDepthContext } from '@/lib/layerManager';
import { PhoneInput } from '../ui/PhoneInput';
import { ConfirmDialog } from '../ui/ConfirmDialog';

export type ContactResolution =
  | { action: 'create'; contact: ContactCreateDTO; assignmentNotes?: string }
  | { action: 'useExisting'; contactId: string; contact?: ContactLookupResultDTO; assignmentNotes?: string }
  | { action: 'createNew'; contact: ContactCreateDTO; assignmentNotes?: string };

interface ContactFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (payload: { contact: ContactCreateDTO | ContactUpdateDTO; assignmentNotes?: string }) => Promise<void> | void;
  onResolution?: (resolution: ContactResolution) => Promise<void> | void;
  onJobSelect?: (jobId: string) => void;
  contact?: ContactDTO;
  companyId?: string;
  title?: string;
  confirmLabel?: string;
  isSubmitting?: boolean;
  includeAssignmentNotes?: boolean;
  initialAssignmentNotes?: string;
  contactFieldsDisabled?: boolean;
  /** Prefill for create mode (ignored when `contact` is set). Runs the phone lookup so the
   *  existing "Match found" choice appears on save. */
  initialValues?: { name?: string; phone?: string; email?: string };
}

interface ContactFormState {
  name: string;
  phone: string;
  additional_phone: string;
  email?: string;
}

const emptyState: ContactFormState = {
  name: '',
  phone: '',
  additional_phone: '',
  email: '',
};

export function ContactFormModal({
  isOpen,
  onClose,
  onSubmit,
  onResolution, // New prop
  onJobSelect,
  contact,
  title = contact ? 'Edit Contact' : 'New Contact',
  confirmLabel = contact ? 'Save Changes' : 'Create Contact',
  isSubmitting = false,
  includeAssignmentNotes = false,
  initialAssignmentNotes = '',
  contactFieldsDisabled = false,
  companyId,
  initialValues,
}: ContactFormModalProps) {
  const [formState, setFormState] = useState<ContactFormState>(emptyState);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [assignmentNotes, setAssignmentNotes] = useState(initialAssignmentNotes);
  const [activePopover, setActivePopover] = useState<'phone' | 'email' | null>(null);
  // Additional Phone stays tucked away behind "+ Phone" until needed.
  const [showAdditionalPhone, setShowAdditionalPhone] = useState(false);
  const [isPastJobsOpen, setPastJobsOpen] = useState(false);
  const [pastJobsContact, setPastJobsContact] = useState<ContactLookupResultDTO | null>(null);
  const [pastJobId, setPastJobId] = useState<string | null>(null);
  const { phoneResults, emailResults, isLoading, lookupPhone, lookupEmail, clearResults } = useContactLookup(companyId);
  const [contactJobCount, setContactJobCount] = useState<number>(0);
  const [pendingDecision, setPendingDecision] = useState<
    | { type: 'sharedContact'; payload: ContactCreateDTO; notes?: string }
    | { type: 'existingMatch'; payload: ContactCreateDTO; notes?: string; match: ContactLookupResultDTO; nameChanged?: boolean }
    | null
  >(null);
  // Form values right after Auto-fill, so save can tell whether the user edited them.
  const [autofilled, setAutofilled] = useState<{ match: ContactLookupResultDTO; values: ContactFormState } | null>(null);
  const layer = useModalLayer({
    enabled: isOpen,
    onClose,
    historyState: { contactFormModal: true },
  });

  useEffect(() => {
    if (contact) {
      setFormState({
        name: contact.name || '',
        phone: contact.phone || '',
        additional_phone: contact.additional_phone || '',
        email: contact.email || '',
      });
      if (isOpen) {
        apiService.getContactJobCount(contact.id).then(res => {
          setContactJobCount(res.job_count);
        }).catch(clientLogger.error);
      }
    } else {
      setFormState({ ...emptyState, ...initialValues });
    }
    setShowAdditionalPhone(!!contact?.additional_phone);
    setAssignmentNotes(initialAssignmentNotes || '');
    setErrors({});
    clearResults();
    setActivePopover(null);
    setPendingDecision(null);
    setAutofilled(null);
    if (!contact && isOpen && initialValues?.phone) lookupPhone(initialValues.phone);
  }, [contact, initialAssignmentNotes, isOpen, initialValues]);

  const handleInputChange = (field: keyof ContactFormState, value: string) => {
    setFormState((prev) => ({
      ...prev,
      [field]: value,
    }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }));
    }

    if (field === 'phone') {
      lookupPhone(value);
      setActivePopover(null);
    } else if (field === 'email') {
      lookupEmail(value);
      setActivePopover(null);
    }
  };

  const validate = () => {
    const validationErrors: Record<string, string> = {};
    if (!contactFieldsDisabled) {
      const hasName = formState.name.trim().length > 0;
      const hasPhone = formState.phone.trim().length > 0;
      const hasEmail = (formState.email?.trim() || '').length > 0;

      // At least one of name, phone, or email must be provided
      if (!hasName && !hasPhone && !hasEmail) {
        validationErrors.name = 'Please provide at least one of: Name, Phone, or Email';
      }
    }
    setErrors(validationErrors);
    return Object.keys(validationErrors).length === 0;
  };

  const handleSubmit = async (event?: React.FormEvent<HTMLFormElement> | React.MouseEvent<HTMLButtonElement>) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation(); // Prevent event from bubbling to parent forms
    }
    if (!validate()) {
      return;
    }

    const payload: ContactCreateDTO = {
      name: formState.name.trim(),
      phone: formState.phone.trim() || '',
      additional_phone: formState.additional_phone.trim() || undefined,
      email: formState.email?.trim() || undefined,
      notes: undefined
    };

    const notes = includeAssignmentNotes ? assignmentNotes.trim() || undefined : undefined;

    // 1. Edit Flow: Check for shared contact
    if (contact) {
      if (contactJobCount > 1 && onResolution) {
        setPendingDecision({ type: 'sharedContact', payload, notes });
        return;
      }

      await onSubmit({
        contact: payload,
        assignmentNotes: notes,
      });
      return;
    }

    // 2. New Contact Flow: Check for existing matches (Smart Matching)
    if (!contact && onResolution) {
      if (autofilled) {
        const original = autofilled.values;
        const unchanged = (Object.keys(original) as (keyof ContactFormState)[]).every(
          (key) => (formState[key] || '').trim() === (original[key] || '').trim()
        );
        if (unchanged) {
          await onResolution({
            action: 'useExisting',
            contactId: autofilled.match.id,
            contact: autofilled.match,
            assignmentNotes: notes,
          });
          return;
        }
        if (formState.name.trim() !== original.name.trim()) {
          setPendingDecision({ type: 'existingMatch', payload, notes, match: autofilled.match, nameChanged: true });
          return;
        }
      }

      const allMatches = [...phoneResults, ...emailResults];
      // Deduplicate by id
      const seen = new Set<string>();
      const matches = allMatches.filter(m => { if (seen.has(m.id)) return false; seen.add(m.id); return true; });

      if (matches.length > 0) {
        const exactMatch = matches.find(m =>
          m.name.toLowerCase() === payload.name.toLowerCase() &&
          (!payload.phone || m.phone === payload.phone) &&
          (!payload.email || m.email === payload.email)
        );

        if (exactMatch) {
          // Silent use existing
          await onResolution({
            action: 'useExisting',
            contactId: exactMatch.id,
            contact: exactMatch,
            assignmentNotes: notes
          });
          return;
        }

        setPendingDecision({ type: 'existingMatch', payload, notes, match: matches[0] });
        return;
      }

      await onResolution({
        action: 'create',
        contact: payload,
        assignmentNotes: notes
      });
      return;
    }

    await onSubmit({
      contact: payload,
      assignmentNotes: notes,
    });
  };

  const handleAutofill = (existing: ContactLookupResultDTO) => {
    const values: ContactFormState = {
      name: formState.name || existing.name,
      phone: formState.phone || existing.phone || '',
      additional_phone: formState.additional_phone || existing.additional_phone || '',
      email: formState.email || existing.email || '',
    };
    setFormState(values);
    if (values.additional_phone) setShowAdditionalPhone(true);
    setAutofilled({ match: existing, values });
    setActivePopover(null);
  };

  const openPastJobs = (existing: ContactLookupResultDTO) => {
    setPastJobsContact(existing);
    setPastJobsOpen(true);
    setActivePopover(null);
  };

  const renderDuplicateIndicator = (results: ContactLookupResultDTO[], field: 'phone' | 'email') => {
    if (!results || results.length === 0) return null;
    const isActive = activePopover === field;

    return (
      <div className="mt-1">
        <button
          type="button"
          onClick={() => setActivePopover(isActive ? null : field)}
          className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2 py-1 text-xs font-medium text-green-800 hover:bg-green-200"
        >
          <UserCheck className="h-3.5 w-3.5" />
          {results.length === 1 ? 'Existing customer' : `${results.length} existing customers`}
          <ChevronDown className={`h-3.5 w-3.5 transition-transform ${isActive ? 'rotate-180' : ''}`} />
        </button>
        {isActive && (
          <div className="mt-2 space-y-2 rounded-lg border border-amber-200 bg-white p-3 shadow-md">
            {results.map((result) => (
              <div key={result.id} className="border-b border-gray-100 pb-2 last:border-b-0 last:pb-0">
                <div className="text-sm font-semibold text-gray-900">{result.name}</div>
                <div className="text-xs text-gray-600 mb-1">
                  {result.phone && <span className="mr-2">📞 {result.phone}</span>}
                  {result.email && <span>✉️ {result.email}</span>}
                </div>
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => handleAutofill(result)}
                    className="rounded-md bg-blue-600 px-3 py-1 text-xs font-medium text-white transition-colors hover:bg-blue-700"
                  >
                    Auto-fill
                  </button>
                  <button
                    type="button"
                    onClick={() => openPastJobs(result)}
                    className="rounded-md border border-gray-200 px-3 py-1 text-xs font-medium text-gray-700 transition-colors hover:bg-gray-100"
                  >
                    View past jobs ({result.jobs?.length ?? 0})
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  if (!isOpen && !pastJobId) return null;

  const portal = isOpen ? createPortal(
    <LayerDepthContext.Provider value={layer.depth}>
    <div
      className={`fixed inset-0 flex items-start justify-center overflow-y-auto overscroll-y-contain bg-black/20 backdrop-blur-sm p-4 pt-[max(1rem,env(safe-area-inset-top,0px))] pb-[max(1rem,var(--safe-area-inset-bottom))] sm:items-center sm:pt-4 sm:pb-4 ${
        pastJobId ? 'hidden' : ''
      }`}
      style={layer.style}
    >
      {!pastJobId && (
        <>
      <div className="mt-[clamp(0.5rem,3vh,1.5rem)] w-full max-h-[min(90vh,90dvh)] max-w-lg overflow-y-auto rounded-2xl bg-white shadow-xl sm:mt-0">
        <div className="flex items-center justify-between border-b border-gray-100 px-6 py-4">
          <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
          <button
            type="button"
            onClick={layer.onClose}
            className="rounded-full p-1 text-gray-400 transition-colors hover:text-gray-600"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="px-6 py-5">
          <div className="space-y-4">
            <div>
              <label htmlFor="contact-name" className="text-sm font-medium text-gray-700">
                Name
              </label>
              <input
                id="contact-name"
                type="text"
                value={formState.name}
                onChange={(event) => handleInputChange('name', event.target.value)}
                disabled={contactFieldsDisabled}
                className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-500"
                placeholder="Contact name"
              />
              {errors.name && <p className="mt-1 text-xs text-red-500">{errors.name}</p>}
            </div>

            <div className="relative">
              <label htmlFor="contact-phone" className="text-sm font-medium text-gray-700">
                Phone
              </label>
              <PhoneInput
                id="contact-phone"
                value={formState.phone}
                onChange={(val) => handleInputChange('phone', val)}
                disabled={contactFieldsDisabled}
                className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-500"
                placeholder="(555) 123-4567"
              />
              {errors.phone && <p className="mt-1 text-xs text-red-500">{errors.phone}</p>}
              {renderDuplicateIndicator(phoneResults, 'phone')}
              {!showAdditionalPhone && !formState.additional_phone && !contactFieldsDisabled && (
                <button
                  type="button"
                  onClick={() => setShowAdditionalPhone(true)}
                  // Sits in the gap under the box (right side) so the field spacing stays even.
                  className="absolute right-0 top-full mt-0.5 inline-flex items-center gap-0.5 text-xs font-medium leading-none text-primary-600 hover:text-primary-700"
                >
                  <Plus className="h-3 w-3" />
                  Phone
                </button>
              )}
            </div>

            {(showAdditionalPhone || !!formState.additional_phone) && (
            <div>
              <label htmlFor="contact-additional-phone" className="text-sm font-medium text-gray-700">
                Additional Phone
              </label>
              <PhoneInput
                id="contact-additional-phone"
                value={formState.additional_phone}
                onChange={(val) => handleInputChange('additional_phone', val)}
                disabled={contactFieldsDisabled}
                className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-500"
                placeholder="(555) 987-6543 (optional)"
              />
            </div>
            )}

            <div>
              <label htmlFor="contact-email" className="text-sm font-medium text-gray-700">
                Email
              </label>
              <input
                id="contact-email"
                type="email"
                value={formState.email || ''}
                onChange={(event) => handleInputChange('email', event.target.value)}
                disabled={contactFieldsDisabled}
                className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-500"
                placeholder="contact@example.com"
              />
              {renderDuplicateIndicator(emailResults, 'email')}
            </div>
          </div>

          {includeAssignmentNotes && (
            <div className="mt-4">
              <label htmlFor="assignment-notes" className="text-sm font-medium text-gray-700">
                Contact Notes
              </label>
              <textarea
                id="assignment-notes"
                value={assignmentNotes}
                onChange={(event) => setAssignmentNotes(event.target.value)}
                className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Notes specific to this job"
                rows={2}
              />
            </div>
          )}

          <div className="mt-6 flex items-center justify-end gap-3">
            <button
              type="button"
              onClick={layer.onClose}
              className="rounded-lg border border-gray-200 px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={isSubmitting}
              className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-70"
            >
              {isSubmitting || isLoading ? 'Saving...' : confirmLabel}
            </button>
          </div>
        </form>
      </div>

      <ContactPastJobsModal
        isOpen={isPastJobsOpen}
        onClose={() => setPastJobsOpen(false)}
        contact={pastJobsContact}
        onJobSelect={(jobId) => {
          setPastJobsOpen(false);
          if (onJobSelect) {
            onClose();
            onJobSelect(jobId);
          } else {
            setPastJobId(jobId);
          }
        }}
      />

      {pendingDecision?.type === 'sharedContact' && (
        <ConfirmDialog
          isOpen
          onClose={() => setPendingDecision(null)}
          title="This contact is used on other jobs"
          message={
            <div className="space-y-2">
              <p>This contact is assigned to {contactJobCount} jobs.</p>
              <p>
                Create a new contact to change details for this job only, or update the shared
                contact to change it on every job.
              </p>
            </div>
          }
          confirmText="Create new contact"
          confirmButtonClassName="bg-blue-600 hover:bg-blue-700 text-white"
          secondaryText="Update shared contact"
          secondaryButtonClassName="bg-white border border-gray-300 text-gray-700 hover:bg-gray-50"
          cancelText="Go back"
          isLoading={isSubmitting}
          onConfirm={async () => {
            if (!onResolution) return;
            await onResolution({
              action: 'createNew',
              contact: pendingDecision.payload,
              assignmentNotes: pendingDecision.notes,
            });
          }}
          onSecondary={async () => {
            await onSubmit({
              contact: pendingDecision.payload,
              assignmentNotes: pendingDecision.notes,
            });
          }}
        />
      )}

      {pendingDecision?.type === 'existingMatch' && (
        <ConfirmDialog
          isOpen
          onClose={() => setPendingDecision(null)}
          title="Existing contact found"
          message={
            <div className="space-y-2">
              <p>
                {pendingDecision.nameChanged
                  ? 'The name has changed from its original contact:'
                  : 'A contact with matching details already exists:'}
              </p>
              <div className="rounded-lg bg-gray-50 px-3 py-2 text-sm">
                <div className="font-medium text-gray-900">{pendingDecision.match.name}</div>
                {pendingDecision.match.phone && (
                  <div className="text-gray-600">{pendingDecision.match.phone}</div>
                )}
                {pendingDecision.match.email && (
                  <div className="text-gray-600">{pendingDecision.match.email}</div>
                )}
              </div>
              <p>Use this contact, or create a new one?</p>
            </div>
          }
          confirmText="Use this contact"
          confirmButtonClassName="bg-blue-600 hover:bg-blue-700 text-white"
          secondaryText="Create new contact"
          secondaryButtonClassName="bg-white border border-gray-300 text-gray-700 hover:bg-gray-50"
          cancelText="Go back"
          isLoading={isSubmitting}
          onConfirm={async () => {
            if (!onResolution) return;
            await onResolution({
              action: 'useExisting',
              contactId: pendingDecision.match.id,
              contact: pendingDecision.match,
              assignmentNotes: pendingDecision.notes,
            });
          }}
          onSecondary={async () => {
            if (!onResolution) return;
            await onResolution({
              action: 'create',
              contact: pendingDecision.payload,
              assignmentNotes: pendingDecision.notes,
            });
          }}
        />
      )}
        </>
      )}
    </div>
    </LayerDepthContext.Provider>,
    document.body
  ) : null;

  return (
    <>
      {portal}
      {pastJobId && createPortal(
        <LayerDepthContext.Provider value={layer.depth}>
          <JobDetailsModal
            isOpen={!!pastJobId}
            onClose={() => setPastJobId(null)}
            jobId={pastJobId}
            openedFromPastJobs={true}
          />
        </LayerDepthContext.Provider>,
        document.body
      )}
    </>
  );
}
